import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import type { SchemaLoanRequestResolver_Get } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { BiShow } from 'react-icons/bi';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator.ts';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { setQueryPosition } from '~/store/queryPositionStore.ts';
import { queryKeys } from '~/utils/queryKeys.ts';

type Props = {
    lead: SchemaLoanRequestResolver_Get;
};

export const RevealCoApplicant: React.FC<Props> = ({ lead }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].revealCoApplicant;
    const { isDirty } = useLeadDetailsFormContext();
    const revealCoApplicant = async () => {
        if (isDirty()) {
            toast.error(t('LEAD_DETAILS_GENERAL.SAVE_BEFORE_REVEALING_APPLICANT'));
            return;
        }
        if (!lead.antragsteller2) {
            toast.error(t('LEAD_DETAILS_GENERAL.CANNOT_HIDE_NOT_EXISTING_APPLICANT'));
            return;
        }
        if (!lead.coApplicantHidden) {
            toast.error(t('LEAD_DETAILS_GENERAL.CANNOT_REVEAL_VISIBLE_APPLICANT'));
            return;
        }
        try {
            const { position } = await APICommunicator.sendCommand({
                aggregateId: lead.id,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.revealCoApplicant,
                payload: {},
            });
            const get = queryKeys.get(lead.id);
            if (position) {
                setQueryPosition(JSON.stringify(get), position);
            }
            queryClient.invalidateQueries({
                queryKey: queryKeys.getAclPermissions({ loanRequestId: lead.id }),
            });
            toast.success(t('LEAD_DETAILS_GENERAL.APPLICANT_REVEALED'));
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <Button
            onClick={async (event) => {
                event.preventDefault();
                await revealCoApplicant();
            }}
            variant={ButtonVariant.SECONDARY}
            iconLeft={<BiShow />}
            disabled={isDisabled}
        >
            <span>{t('CREDIT_WORTHINESS.BUTTONS.REVEAL_SECOND_APPLICANT')}</span>
        </Button>
    );
};
